import { defineStore, acceptHMRUpdate } from 'pinia';
import values from 'lodash/values';
import keyBy from 'lodash/keyBy';
import {
    addDomain,
    deleteDomain,
    fetchDomainVerificationRecords,
    refreshVerification,
    verifyDomain,
} from '@/vuex/modules/domains/domains.utils';
import { useOrganizationStore } from '@/stores/organization.store';

interface Domain {
    id: string;
    domainName?: string;
    isActive?: boolean;
    [key: string]: unknown;
}

interface DomainsState {
    map: Record<string, Domain>;
}

function getInitialState (): DomainsState {
    return {
        map: {},
    };
}

let loadAllPromise: Promise<void> | null = null;

export const useDomainsStore = defineStore('domains', {
    state: getInitialState,
    getters: {
        list: (state): Domain[] => values(state.map),
        getById: (state) => (id: string): Domain | undefined => state.map[id],
    },
    actions: {
        async loadAll ({ reFetch = false }: { reFetch?: boolean } = {}) {
            if (loadAllPromise && reFetch !== true) {
                await loadAllPromise;

                return;
            }

            try {
                const orgId = useOrganizationStore().getActive.id;
                loadAllPromise = fetchDomainVerificationRecords(orgId).then((domains: Domain[]) => {
                    this.map = keyBy(domains, 'id');
                });
                await loadAllPromise;
            } catch (error) {
                loadAllPromise = null;
                throw error;
            }
        },
        async verifyDomain ({ targetUrl }: { targetUrl?: string } = {}) {
            const existingRecord = this.list.find((domain) => domain.domainName === targetUrl);
            if (existingRecord?.isActive) return true;

            const orgId = useOrganizationStore().getActive.id;
            const verified = await verifyDomain(orgId, targetUrl);

            return verified;
        },
        async add ({ targetUrl }: { targetUrl?: string } = {}) {
            const orgId = useOrganizationStore().getActive.id;
            const domain = await addDomain(orgId, targetUrl);
            this.map[domain.id] = domain;

            return domain;
        },
        async refresh ({ domainId }: { domainId?: string } = {}) {
            const orgId = useOrganizationStore().getActive.id;
            const domain = await refreshVerification(orgId, domainId);
            this.map[domainId as string] = domain;

            return domain;
        },
        async remove ({ domainId }: { domainId?: string } = {}) {
            const orgId = useOrganizationStore().getActive.id;
            await deleteDomain(orgId, domainId);
            delete this.map[domainId as string];
        },
    },
});

export function __resetLoadAllPromiseForUnitTests () {
    loadAllPromise = null;
}

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useDomainsStore, module.hot));
}
